/**
 * API 엔드포인트·상수 (설계 §2-2(4)).
 *
 * 서비스 context-path 가 `/api` 라 baseURL 은 `${VITE_API_URL}/api`,
 * 엔드포인트는 `/member/v1/...` · `/venue/v1/manager/...` 이다.
 *
 * 🚧 **dev 서버로 띄웠을 때만** 상대경로 `/api` 를 써서 Vite proxy 를 태운다.
 * facecock-was 의 dev CORS allowed-origins 에 localhost 가 없어(실측) 브라우저에서
 * 직접 호출하면 preflight 에서 막히기 때문이다.
 *
 * ⚠️ 판정에 `import.meta.env.DEV` 를 쓰면 안 된다 — `vite build --mode development`
 * (= `build:dev`, dev Hosting 배포본)에서도 참이 되어 배포본이 프록시 없는 상대경로를
 * 호출하게 된다. 프록시는 dev 서버에만 존재하므로 `__DEV_SERVER__` 로 주입한다.
 */
export const API_BASE_URL: string = __DEV_SERVER__
  ? '/api'
  : `${import.meta.env.VITE_API_URL ?? ''}/api`

export const VENUE_MANAGER_PREFIX = '/venue/v1/manager'

/**
 * 구장 관리자 **전용** 인증 표면 (#583).
 *
 * 🚨 일반 회원 로그인(`/member/v1/auth/**`)이 아니다. 발급되는 토큰은 같은 회원 JWT 지만
 * 이 EP 만 ⓐ 실패 사유(`reason`)를 코드로 구분해 주고, ⓑ 관리 구장 목록을 함께 내려주며,
 * ⓒ **비밀번호를 Base64 로 받는다**(평문을 보내면 400 `INVALID_PASSWORD_ENCODING`).
 *
 * `login`·`refresh` 는 permitAll, **`logout` 은 인증 필요**다 — `isAuthFreePath` 에 넣지 말 것.
 */
export const AUTH_PATHS = {
  login: `${VENUE_MANAGER_PREFIX}/auth/login`,
  refresh: `${VENUE_MANAGER_PREFIX}/auth/refresh-token`,
  logout: `${VENUE_MANAGER_PREFIX}/auth/logout`,
} as const

/**
 * 내 구장 컨텍스트 (#584 §1).
 * 🚨 관리자 표면에서 **유일하게 경로 변수가 없는** EP 다 — `venueId` 를 얻는 입구이기 때문이다.
 */
export const MANAGED_VENUES_PATH = `${VENUE_MANAGER_PREFIX}/venues`

/**
 * 구장 대시보드 요약 (#587).
 * 🚨 이용권이 모두 만료돼도 게이팅 대상이 아니다 — 항상 200.
 */
export function venueDashboardPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/dashboard`
}

/**
 * 구장 기본정보 조회·수정 (#594).
 * 🚨 `VENUE_PROFILE_EDIT` 게이트가 `ALWAYS_OPEN` 이다 — 이용권이 모두 만료돼도 조회·저장 둘 다 200.
 */
export function venueProfilePath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/profile`
}

/**
 * 구장 코트 목록·등록·수정·삭제 (#597).
 * 🚨 자원명은 `venues/{venueId}/courts` 다 — `gyms/{gymId}` 아님. 목록 EP 는 게이팅 대상이 아니라
 * 이용권이 모두 만료돼도 200(무엇이 잠겼는지는 응답의 `editable` 로 안다). 쓰기 3EP 는 게이팅 대상.
 */
export function venueCourtsPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/courts`
}

export function venueCourtPath(venueId: string, courtId: number): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/courts/${courtId}`
}

/**
 * 코트 운영시간 조회·저장 (#600).
 * 🚨 조회는 게이팅 대상이 아니다 — 이용권이 만료돼도 200(무엇이 잠겼는지는 응답의 `editable` 로 안다).
 * 저장은 게이팅 대상(404→403→409).
 */
export function venueCourtOperatingHoursPath(venueId: string, courtId: number): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/courts/${courtId}/operating-hours`
}

/**
 * 코트 요일유형 요금 구간 조회·저장 (#852 §2). `GET`·`PUT`.
 * 🚨 조회는 게이팅 대상이 아니다 — 이용권이 만료돼도 200(무엇이 잠겼는지는 응답의 `editable` 로 안다).
 * 저장은 게이팅 대상(404→403→409)이며 **전체 치환**이다(`bands: []` 면 구간이 모두 삭제된다).
 */
export function venueCourtPriceBandsPath(venueId: string, courtId: number): string {
  return `${venueCourtPath(venueId, courtId)}/price-bands`
}

/**
 * 아직 요금 구간이 설정되지 않은 잔여 시간대 조회 (#866 API ⑤). `GET`.
 * `?dayType=` 또는 `?date=` 중 **하나만** 필수(동시 지정은 400). 🚨 이용권이 만료돼도 200.
 */
export function venueCourtPriceBandRemainingPath(venueId: string, courtId: number): string {
  return `${venueCourtPriceBandsPath(venueId, courtId)}/remaining-windows`
}

/**
 * 코트 날짜 지정 요금 목록 조회 (#866 API ④). `GET`, `from`/`to` 최대 366일(생략 시 오늘~+90일).
 * 🚨 요일유형 축(`price-bands`)과 **다른 자원**이다 — 날짜 규칙이 요일유형 규칙을 덮는다(3단 우선순위).
 */
export function venueCourtPriceDatesPath(venueId: string, courtId: number): string {
  return `${venueCourtPath(venueId, courtId)}/price-dates`
}

/**
 * 날짜 1건 저장·삭제 (#866 API ④). `PUT`(그 날짜 전량 교체·멱등)·`DELETE`.
 * 🚨 과거 날짜 저장은 400 이다(KST 오늘 포함 미래만).
 */
export function venueCourtPriceDatePath(venueId: string, courtId: number, date: string): string {
  return `${venueCourtPriceDatesPath(venueId, courtId)}/${date}`
}

/**
 * 휴무·차단·레슨 목록 조회·등록 (#604).
 * 🚨 목록 조회는 게이팅 대상이 아니다 — 이용권이 만료돼도 200(무엇이 잠겼는지는 응답의 `editable` 로 안다).
 * 등록은 게이팅 대상(404→403→409). 필터 쿼리 파라미터는 보내지 않는다 — 무필터 단일 쿼리를 클라이언트가
 * 필터링한다(설계 §2 자율 결정 A1).
 */
export function venueClosuresPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/closures`
}

/** 휴무·차단·레슨 해제(소프트 삭제) (#604). 게이팅 대상(404→403→409). */
export function venueClosurePath(venueId: string, closureId: number): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/closures/${closureId}`
}

/**
 * 예약 취소·환불 월별 집계 (#589).
 * 🚨 대시보드 EP 의 `cancellations` 와는 별개의 EP 다 — 그쪽은 Phase 1 에서 항상 `null` 이지만
 * 이 EP 는 확정 계약으로 이미 실데이터를 준다(설계 §2 자율 결정 2).
 */
export function venueReservationRefundSummaryPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservation-refunds/summary`
}

/**
 * 예약 캘린더 — 기간별 예약 요약(월간·주간) (#666 §1).
 * 🚨 게이팅 대상이 아니다 — `RESERVATION_VIEW` 는 `ALWAYS_OPEN`(이용권이 모두 만료돼도 항상 200).
 */
export function venueReservationCalendarPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservation-calendar`
}

/**
 * 예약 현황판 — 특정 날짜 코트별 시간대 현황 (#666 §2).
 * 🚨 게이팅 대상이 아니다 — `RESERVATION_VIEW` 는 `ALWAYS_OPEN`.
 */
export function venueReservationBoardPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservation-board`
}

/**
 * 예약 목록 (#666 §3, 커서 페이지네이션).
 * 🚨 게이팅 대상이 아니다 — `RESERVATION_VIEW` 는 `ALWAYS_OPEN`.
 */
export function venueReservationsPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservations`
}

/**
 * 예약 단건 상세 (#666 §4, 5블록).
 * 🚨 게이팅 대상이 아니다 — `RESERVATION_CANCEL_REFUND` 도 `ALWAYS_OPEN`(만료된 구장도 취소·환불
 * 대상 예약을 볼 수 있어야 한다).
 */
export function venueReservationPath(venueId: string, reservationId: number): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservations/${reservationId}`
}

/**
 * 예약자 연락(tel) — 원본 연락처 전용 EP (#666 §5).
 * 🚨 호출 자체가 열람 기록으로 남는다 — 화면 진입 시 미리 호출하지 말고 버튼 탭 시점에만 호출한다.
 */
export function venueReservationBookerContactPath(venueId: string, reservationId: number): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservations/${reservationId}/booker-contact`
}

/**
 * 예약 변경 — 코트·날짜·시간 이동 (#669 §1). `PATCH`.
 * 🚨 게이팅 대상이다(`RESERVATION_MANAGE`) — 조회 4종과 달리 이용권이 만료되면 409.
 */
export function venueReservationSchedulePath(venueId: string, reservationId: number): string {
  return `${venueReservationPath(venueId, reservationId)}/schedule`
}

/**
 * 환불 예상액 조회 (#672 관리자 표면). `GET`.
 * 🚨 게이팅 대상이 아니다(`RESERVATION_CANCEL_REFUND` 는 `ALWAYS_OPEN`) — 이용권이 만료돼도 항상 200.
 * 🚨 `baseTime` 기준 예상치다 — 캐시해서 취소 실행에 재사용하지 않는다.
 */
export function venueReservationRefundEstimatePath(venueId: string, reservationId: number): string {
  return `${venueReservationPath(venueId, reservationId)}/refund-estimate`
}

/**
 * 예약 취소 처리 (#672 관리자 표면). `POST`, `Idempotency-Key` 헤더 필수.
 * 🚨 게이팅 대상이 아니다(`RESERVATION_CANCEL_REFUND` 는 `ALWAYS_OPEN`) — #669 예약 변경과 반대로
 * 이용권이 만료돼도 항상 처리된다(사용자 돈이 묶이지 않게).
 */
export function venueReservationCancelPath(venueId: string, reservationId: number): string {
  return `${venueReservationPath(venueId, reservationId)}/cancel`
}

/**
 * 매출 현황(기간 축) — 이번달/지난달/직접지정 (#703 §2). `GET`.
 * 🚨 게이팅 대상이 아니다(`SETTLEMENT_VIEW` 는 `ALWAYS_OPEN`) — 이용권이 만료돼도 항상 200.
 */
export function venueSettlementRevenuePath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/settlements/revenue`
}

/**
 * 정산 회차 목록 — 커서 페이지네이션(#703 §3). `GET`.
 * 🚨 게이팅 대상이 아니다. 커서는 회차월(`YYYY-MM`) 그 자체이며 불투명 토큰이 아니다 — 그대로 되싣는다.
 */
export function venueSettlementCyclesPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/settlements/cycles`
}

/** 정산 회차 CSV 다운로드(#703 §6). `GET`, `statuses` 필터 없음(대사용 전 회차). */
export function venueSettlementCyclesExportPath(venueId: string): string {
  return `${venueSettlementCyclesPath(venueId)}/export`
}

/** 분쟁환불 차감 내역 — 커서 페이지네이션(#703 §4). `GET`. 게이팅 대상이 아니다. */
export function venueSettlementDeductionsPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/settlements/deductions`
}

/**
 * 매출 현황(예약 축) — 결제 시점 수수료 스냅샷 기준, 코트·상태 필터 + 예약별 목록(#792 §2). `GET`.
 * 🚨 게이팅 대상이 아니다. `venueSettlementRevenuePath`(회차 축)와 **다른 EP** — 혼동 금지(§0-①).
 */
export function venueRevenuePath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/revenues`
}

/**
 * 대시보드 "이번 달 매출 요약"(#792 §3). `GET`, 파라미터 없음 — 서버가 KST "이번 달"을 판정한다.
 * 🚨 게이팅 대상이 아니다.
 */
export function venueRevenueDashboardPath(venueId: string): string {
  return `${venueRevenuePath(venueId)}/dashboard`
}

/**
 * 환불 처리 결과 목록(#703 §5, #589 §4-2 확장) — 커서 페이지네이션. `GET`.
 * 🚨 기존 `venueReservationRefundSummaryPath`(월별 집계)와 별개 EP — 이 경로는 건별 목록이다.
 */
export function venueReservationRefundsPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservation-refunds`
}

/** 환불 처리 결과 CSV 다운로드(#703 §6). `GET`, 목록과 동일 필터를 그대로 전달한다. */
export function venueReservationRefundsExportPath(venueId: string): string {
  return `${venueReservationRefundsPath(venueId)}/export`
}

/**
 * 공지·홍보 발송(campaign) — 작성·목록·상세·대상 산출 (#714). `venues/{venueId}/campaigns` 기준.
 * 🚨 목록·상세·대상 산출·발송·결과·수신자는 모두 이 자원 하위 경로다 — `notice-dispatches`(구
 * `venue_notice_dispatch` 기반, #728 §7 폐기 예정)를 쓰지 않는다.
 */
export function venueCampaignsPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/campaigns`
}

/**
 * 공지·홍보 단건(상세) — `GET`(게이팅 없음) · `PUT`(내용 수정, #714 §1-6·#732 — DRAFT 전용으로 화면을
 * 제한한다. `SENT`·`SENDING`·`FAILED`는 409(`40938`)).
 */
export function venueCampaignPath(venueId: string, campaignId: number): string {
  return `${venueCampaignsPath(venueId)}/${campaignId}`
}

/** 예약 확정/변경(#714 §4-2, #732). `PUT`. `{"scheduledAt":"yyyy-MM-ddTHH:mm"}`(KST 벽시계, 오프셋 금지). */
export function venueCampaignSchedulePath(venueId: string, campaignId: number): string {
  return `${venueCampaignPath(venueId, campaignId)}/schedule`
}

/** 대상 산출·예상 비용(#714 §1-7). `GET`. 작성과 같은 게이트(`FOLLOWER_NOTICE`/`REGION_PROMOTION`). */
export function venueCampaignEstimatePath(venueId: string): string {
  return `${venueCampaignsPath(venueId)}/estimate`
}

/** 발송 실행(#715 §D-1). `POST`. `202`(통과)/`200`(차단) 둘 다 성공 응답이다. */
export function venueCampaignSendPath(venueId: string, campaignId: number): string {
  return `${venueCampaignPath(venueId, campaignId)}/send`
}

/** 이력 요약(#728 §3). `GET`, 게이팅 없음(`ALWAYS_OPEN`). `from`/`to` 필수, `type` 생략 시 전 유형 합산. */
export function venueCampaignSummaryPath(venueId: string): string {
  return `${venueCampaignsPath(venueId)}/summary`
}

/** 발송 결과(#715 §D-2, #728 §5). `GET`, 게이팅 없음. `charge`는 `NOTICE`면 항상 `null`. */
export function venueCampaignResultPath(venueId: string, campaignId: number): string {
  return `${venueCampaignPath(venueId, campaignId)}/result`
}

/** 대상별 읽음 목록(#728 §6). `GET`, 게이팅 없음. `maskedName`만 내려온다(연락처 없음). */
export function venueCampaignRecipientsPath(venueId: string, campaignId: number): string {
  return `${venueCampaignPath(venueId, campaignId)}/recipients`
}

/** 홍보 성과·과금 요약(#734 §1-2, #736). `GET`, 게이팅 없음(`ALWAYS_OPEN`) — 이용권 만료와 무관하게 항상 200. */
export function venueCampaignPerformanceSummaryPath(venueId: string): string {
  return `${venueCampaignsPath(venueId)}/performance/summary`
}

/**
 * 홍보 성과·과금 목록(#734 §1-3, #736 §1) — 커서 페이지네이션, `SENT`+`PROMOTION`만 담는다.
 * 🚨 `campaigns`(작성일 축) 목록과 축·커서가 다르다 — 한쪽 커서를 다른 쪽에 넣지 않는다.
 */
export function venueCampaignPerformancePath(venueId: string): string {
  return `${venueCampaignsPath(venueId)}/performance`
}

/** 홍보 성과 CSV 다운로드(#734 §1-4, #736 §2-3). `GET`, `responseType:'blob'`. 목록과 같은 조건 해석기. */
export function venueCampaignPerformanceExportPath(venueId: string): string {
  return `${venueCampaignPerformancePath(venueId)}/export`
}

/** 운영 공지 전용 열람 현황(#734 §1-5). `GET`. 🔒 비용·전환 필드가 스키마 자체에 없다(null이 아니다). */
export function venueNoticeReadStatusPath(venueId: string): string {
  return `${venueCampaignsPath(venueId)}/notice-read-status`
}

/**
 * 예약 기능 사용 설정 조회·전환(#758, 백엔드 #757). `GET`·`PUT`.
 * 🚨 게이팅 대상이 아니다 — 인증 미완료·조건 미충족이어도 항상 200(이 화면이 예약을 켜러 가는
 * 유일한 입구라 상태를 이유로 막으면 영구 잠금이 된다). `PUT` 은 켜기인데 조건 미충족일 때만 409.
 */
export function venueReservationSettingPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/reservation-setting`
}

/** 예약 기능 사용 설정 변경 이력(#758, 백엔드 #757). `GET`, 커서 페이지네이션. 게이팅 없음. */
export function venueReservationSettingHistoriesPath(venueId: string): string {
  return `${venueReservationSettingPath(venueId)}/histories`
}

/**
 * 운영 인증 신청 제출(#772, 백엔드 #771). `POST`. `#770` EP-A(상태 조회 `GET`)와 **같은 경로**다 —
 * 이 저장소는 아직 `GET`(상태 조회 화면)을 소비하지 않는다.
 * 🚨 게이팅 없음 — 인증하러 오는 입구라 어떤 상태에서도 열린다(`#771` §1).
 */
export function venueOperationVerificationPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/operation-verification`
}

/** 인증 서류 업로드 URL 발급(#772, 백엔드 #771 §3). `POST`. 응답의 `uploadUrl` 로 S3 에 직접 `PUT` 한다. */
export function venueOperationVerificationUploadUrlPath(venueId: string): string {
  return `${venueOperationVerificationPath(venueId)}/documents/upload-url`
}

/** 직전 신청 값 조회 — 재신청 prefill(#772, 백엔드 #771 §4). `GET`. 이력이 없어도 200(`hasPrevious=false`). */
export function venueOperationVerificationPrefillPath(venueId: string): string {
  return `${venueOperationVerificationPath(venueId)}/prefill`
}

/**
 * 운영 인증 이력(회차) 목록(#778, 백엔드 #777 §3). `GET`, 커서 페이지네이션.
 * 🚨 게이팅 없음 — 신청한 적 없으면 `applications: []` + 200(404 아님).
 */
export function venueOperationVerificationApplicationsPath(venueId: string): string {
  return `${venueOperationVerificationPath(venueId)}/applications`
}

/**
 * 사업자 정보 수정 요청(#1191, 백엔드 #1189 §D7) — 운영 인증 신청(`operation-verification`)과 별개
 * 축이다. `GET`은 현행값 + 대기중 요청 + 최근 반려 사유, `POST`는 수정 요청 접수.
 * 🚨 게이팅 없음 — 인증 미완료·만료·반려 어느 상태에서도 200(api-spec §D7 "M1~M4 전부 게이팅 없음").
 */
export function venueBusinessInfoPath(venueId: string): string {
  return `${VENUE_MANAGER_PREFIX}/venues/${venueId}/business-info`
}

/** 사업자 정보 수정 요청 접수(#1191, 백엔드 #1189 §D7-M1). `POST`. */
export function venueBusinessInfoChangeRequestsPath(venueId: string): string {
  return `${venueBusinessInfoPath(venueId)}/change-requests`
}

/** 만료 이 시간 이내면 선제 갱신한다. */
export const EXPIRY_SKEW_MS = 60_000

/**
 * refresh 전용 클라이언트 타임아웃.
 * ⚠️ 없으면 갱신 in-flight 프라미스가 영구 pending 이 되어 대기 중인 전 요청이 무기한 정지한다.
 */
export const REFRESH_TIMEOUT_MS = 10_000

export const DEFAULT_TIMEOUT_MS = 10_000

/**
 * 로그아웃 EP 타임아웃.
 * 🚨 짧게 잡는다 — 서버가 응답하지 않아도 사용자를 로그인 화면으로 보내야 한다(설계 D7·R8).
 */
export const LOGOUT_TIMEOUT_MS = 3_000

export const REFRESH_LOCK_KEY = 'fc-gym-admin:refresh'

/** `OsType` enum 에 `WEB` 이 존재한다(실측). */
export const OS_TYPE = 'WEB'

/**
 * 🚨 T3 — `App-Version` 은 컨트롤러가 `required = false` 로 선언했지만 Kotlin 파라미터가
 * non-null `String` 이라 **헤더가 없으면 500** 이다. 항상 보낸다.
 */
export const APP_VERSION = __APP_VERSION__

/** 이력 조회 기본 페이지 크기(서버도 20). 서버가 1~50 으로 보정한다. */
export const HISTORY_PAGE_SIZE = 20

/**
 * 토큰을 주입하지 않는 경로 — 인터셉터 재귀·루프 방지.
 * ⚠️ 부분일치가 아니라 **경로 끝 일치**로 판정한다.
 *
 * 🚨 **`AUTH_PATHS.logout` 을 여기 넣지 않는다.** 로그아웃은 서버가 `authenticated()` 로 보호하며
 * principal 로 `memberId` 를 읽어 그 디바이스 세션만 지운다 — 토큰이 빠지면 401 이 되어
 * 서버 refresh 행이 만료까지 살아남는다.
 */
export function isAuthFreePath(url: string | undefined): boolean {
  if (!url) return false
  const path = url.split('?')[0] ?? ''
  return path.endsWith(AUTH_PATHS.login) || path.endsWith(AUTH_PATHS.refresh)
}
